import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FikoYT3dPolylineDialogComponent } from './fikoyt-3d-polyline-dialog.component';
import { FikoYT3dPolyline } from './fikoyt-3d-polyline';
import { FikoSahne } from '../../FikoSahne';

/**
 * 3D Polyline Toolbar Button Component
 *
 * Araç çubuğuna eklenebilen 3D polyline oluşturma butonu.
 * Tıklandığında polyline oluşturma dialogunu açar.
 *
 * Kullanım:
 * ```html
 * <fikoyt-3d-polyline-toolbar-button
 *   [sahne]="fikoSahne"
 *   (polylineOlusturuldu)="handlePolyline($event)">
 * </fikoyt-3d-polyline-toolbar-button>
 * ```
 */
@Component({
    selector: 'fikoyt-3d-polyline-toolbar-button',
    template: `
        <!-- Toolbar Button -->
        <button class="fiko-3d-polyline-btn"
                [class.active]="dialogAcik"
                (click)="dialogAcKapa()"
                title="3D Polyline Oluştur">
            <!-- SVG Icon - Polyline -->
            <svg viewBox="0 0 24 24"
                 width="18"
                 height="18"
                 fill="none"
                 stroke="currentColor"
                 stroke-width="2"
                 stroke-linecap="round"
                 stroke-linejoin="round">
                <!-- Polyline çizgisi -->
                <polyline points="2,18 6,10 12,14 18,6 22,10"/>
                <!-- Köşe noktaları -->
                <circle cx="2" cy="18" r="2" fill="currentColor" stroke="none"/>
                <circle cx="6" cy="10" r="2" fill="currentColor" stroke="none"/>
                <circle cx="12" cy="14" r="2" fill="currentColor" stroke="none"/>
                <circle cx="18" cy="6" r="2" fill="currentColor" stroke="none"/>
                <circle cx="22" cy="10" r="2" fill="currentColor" stroke="none"/>
            </svg>
        </button>

        <!-- Dialog Component -->
        <fikoyt-3d-polyline-dialog
            #polylineDialog
            [sahne]="sahne"
            [gorunur]="dialogAcik"
            (polylineOlusturuldu)="polylineOlusturulduHandler($event)"
            (kapandi)="dialogKapandiHandler()">
        </fikoyt-3d-polyline-dialog>
    `,
    styles: [`
        :host {
            display: inline-block;
        }

        .fiko-3d-polyline-btn {
            width: 28px;
            height: 28px;
            border: 1px solid rgba(0, 255, 255, 0.3);
            border-radius: 6px;
            background: linear-gradient(135deg, rgba(0, 60, 80, 0.8), rgba(0, 40, 50, 0.9));
            color: rgba(0, 255, 255, 0.9);
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            padding: 0;

            &:hover {
                background: linear-gradient(135deg, rgba(0, 80, 100, 0.9), rgba(0, 60, 70, 0.95));
                border-color: rgba(0, 255, 255, 0.6);
                box-shadow: 0 0 12px rgba(0, 255, 255, 0.4);
                transform: scale(1.05);

                svg {
                    filter: drop-shadow(0 0 4px rgba(0, 255, 255, 0.6));
                }
            }

            &:active {
                transform: scale(0.98);
            }

            &.active {
                background: linear-gradient(135deg, rgba(0, 120, 140, 0.95), rgba(0, 80, 100, 0.95));
                border-color: rgba(0, 255, 255, 0.8);
                box-shadow: 0 0 15px rgba(0, 255, 255, 0.5);
                color: #00ffff;

                svg {
                    filter: drop-shadow(0 0 6px rgba(0, 255, 255, 0.8));
                }
            }

            svg {
                transition: filter 0.2s ease;
            }
        }
    `],
    standalone: true,
    imports: [CommonModule, FikoYT3dPolylineDialogComponent]
})
export class FikoYT3dPolylineToolbarButtonComponent {

    /** Sahne referansı */
    @Input() sahne?: FikoSahne;

    /** Polyline oluşturulduğunda emit */
    @Output() polylineOlusturuldu = new EventEmitter<FikoYT3dPolyline>();

    /** Dialog referansı */
    @ViewChild('polylineDialog') polylineDialog?: FikoYT3dPolylineDialogComponent;

    /** Dialog açık mı? */
    dialogAcik: boolean = false;

    /**
     * Dialog aç/kapa toggle
     */
    dialogAcKapa(): void {
        this.dialogAcik = !this.dialogAcik;
        if (this.dialogAcik && this.polylineDialog) {
            this.polylineDialog.ac();
        }
    }

    /**
     * Polyline oluşturulduğunda
     */
    polylineOlusturulduHandler(polyline: FikoYT3dPolyline): void {
        this.polylineOlusturuldu.emit(polyline);
        this.dialogAcik = false;

        // Sahneye ekle (zaten dialog içinde ekleniyor ama emin olmak için)
        if (this.sahne && polyline.parent !== this.sahne) {
            this.sahne.add(polyline);
        }
    }

    /**
     * Dialog kapandığında
     */
    dialogKapandiHandler(): void {
        this.dialogAcik = false;
    }
}

/**
 * 3D Polyline SVG Icon (standalone kullanım için)
 */
export const POLYLINE_3D_ICON_SVG = `
<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="2,18 6,10 12,14 18,6 22,10"/>
    <circle cx="2" cy="18" r="2" fill="currentColor" stroke="none"/>
    <circle cx="6" cy="10" r="2" fill="currentColor" stroke="none"/>
    <circle cx="12" cy="14" r="2" fill="currentColor" stroke="none"/>
    <circle cx="18" cy="6" r="2" fill="currentColor" stroke="none"/>
    <circle cx="22" cy="10" r="2" fill="currentColor" stroke="none"/>
</svg>
`;
